import {Language, Occupancy} from "@variocube/app-api";
import {DateTime} from "luxon";
import {naturalCompare} from "@variocube/app-common";
import {TableSort} from "@variocube/cube-ui";
import { v4 as uuidv4 } from 'uuid';


export enum OccupancyType {
    Outbound = "Outbound",
    Inbound = "Inbound",
}

export interface Delivery {
    metadata?: ForeignSystemMetadata;
    order?: Order;
    sender?: Sender;
    recipients: Recipient[];
    bookmark?: string;
    storage: Storage;
    delivery: DeliveryMetadata;
    returnDelivery?: ReturnDelivery;
    tags?: string[];
    learnedPickupKey?: string;
    usedPickupKey?: string;
}

export interface ForeignSystemMetadata {
    foreignId: string;
}

export interface Order {
    orderId: string;
    description: string;
    deliveryNoteId?: string;
}

export interface Recipient {
    id?: string;
    recipientName: string;
    department?: string;
    email?: string;
    phone?: string;
    pickupKey?: string;
    learnedPickupKey?: string;
    centerId?: string;
    position?: string;
    employeeNumber?: string;
    locale?: string;
}

export interface Sender { 
    name: string; 
    email?: string;
    phone?: string; 
    centerId?: string; 
    employeeNumber?: string;
    locale?: string;
}

export interface Storage {
    storageTimeMax?: number;
    hazardousGoods?: string;
    destination?: string;
    storageKey?: string;
    storageCode?: string;
}

export interface DeliveryMetadata {
    deliveryId: string;
    isCreatedOnCube: boolean;
    state: DeliveryState;
    // cube: string;
    boxNumber?: string;
    allowPickupUntil?: string;
    // createdAt: string;
    storedAt?: string;
    pickedUpAt?: string;
    accessCode?: string;
    deliveryType?: string;

    storedBy?: string;
    pickedUpBy?: string;
}

export interface ReturnDelivery {
    description: string;
    state: DeliveryState;
    deliveryNoteId? : string;
    customerId?: string;
    positionNumbers?: string;
    reasonForReturn?: string; 
    returningCustomer?: Recipient;
}

export enum DeliveryState {
    Draft = "Draft",
    Created = "Created",
    Reserved = "Reserved",
    Stored = "Stored",
    PickedUp = "PickedUp",
    Cancelled = "Cancelled",
    Returned  = "Returned",
    ManualHandover = "ManualHandover",
    Shipped = "Shipped"
}

export enum UseExistingBox {
    Disabled = 'Disabled',
    AskToAdd = 'AskToAdd',
    Always = 'Always',
}

export interface Tenant {
    centerId: string;
    name: string;
    users: TenantUser[];
    allowSplitStorage?: boolean;
    allowPartialPickup?: boolean;
    gracePeriod?: number;
    nfcCardLearning?: boolean;
    checkAfterPickup?: boolean;
    createDeliveryFromCode?: boolean;
    returnDeliveryConfig?: ReturnDeliveryConfig;
    useExistingBox?: UseExistingBox;
	storageCountDown: number|null;
}

export interface DeliveryFieldConfig {
    enabled: boolean;
    mandatory: boolean;
    verification?: boolean;
}

export interface ReturnDeliveryConfig {
    enabled: boolean;
    returnRequiresAuthorization: boolean;
    authorizationProviderHint?: string;
    parameters?: {
        deliveryNoteId?: DeliveryFieldConfig;
        returnReason?: DeliveryFieldConfig;
        email?: DeliveryFieldConfig;
        phone?: DeliveryFieldConfig;
        customerId?: DeliveryFieldConfig;
        positionNumber?: DeliveryFieldConfig;
        additionalInformation?: DeliveryFieldConfig;
    }
}

export function getReturnDeliveryConfig(tenant: Tenant): ReturnDeliveryConfig {
    return tenant.returnDeliveryConfig ?? {
        enabled: false,
        returnRequiresAuthorization: false,
        parameters: {}
    };
}

export interface TenantUser {
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    accessCode: string;
}

export enum BookmarkType {
	Department = 'Department',
	Outbound = 'Outbound',
}

export interface Bookmark {
    id: string;
    type: BookmarkType;
    label: string;
    recipients: Recipient[]; 
    description?: string;
    cubeId?: string;
	requireConfirmation?: boolean;
	requireInternalRecipient?: boolean;
	requireRecipientAddress?: boolean;
	requireDispatchDetails?: boolean;
    requireDescription?: boolean;
    enableAnonymousDelivery?: boolean;
    requireAuthorization?: boolean;
    authorizationProviderHint?: string;
	fixedPickupCode?: string;
}

export interface PickupEvent {
    occupancies: Occupancy[];
    pickupKey?: string;
    pickupRecipient: Recipient|undefined;
}

export enum ReasonForReturnEnum {
    WrongItem="WrongItem",
    DefectItem= "DefectItem"
}

export function getOccupancyDeliveries(occupancy: Occupancy) {
    const content = occupancy?.content;
    if (content?.deliveries) {
        return content.deliveries as Delivery[];
    }
    if (content?.delivery && content?.recipients && content?.storage) {
        return [content as Delivery];
    }
}

export interface LearnedPickupKey {
    recipientIndex: number;
    pickupKey: string;
}

export function isDeliveryOverdue(delivery: Delivery): boolean {
    if (delivery.delivery.state == DeliveryState.Stored && delivery.delivery.allowPickupUntil) {
        const now = DateTime.local();
        const allowPickupUntil = DateTime.fromISO(delivery.delivery.allowPickupUntil);
        return allowPickupUntil <= now;
    }
    return false;
}

export function isDeliveryToBePickedUp(delivery: Delivery): boolean {
    const outboundDelivery = delivery.delivery.state == DeliveryState.Stored && delivery.delivery.deliveryType == OccupancyType.Outbound;
    const returnDelivery = delivery.delivery.state == DeliveryState.Returned;
    const storageTimeExceeded = isStorageTimeExceeded(delivery);
    return outboundDelivery || returnDelivery || storageTimeExceeded;
}

function isStorageTimeExceeded(delivery: Delivery) {
    if (delivery.delivery.allowPickupUntil) {
        const now = DateTime.now();
        const allowPickupUntil = DateTime.fromISO(delivery.delivery.allowPickupUntil);
        return allowPickupUntil <= now;
    }
    return false;
}

export function formatDateTime(dateTime: string | undefined, language: string): string {
    const storedAtDateTime = DateTime.fromISO(dateTime || '').setLocale(language === Language.EN ? "en-US" : language);
    const formatted = storedAtDateTime.isValid ? storedAtDateTime.toLocaleString(DateTime.DATE_SHORT) + ' ' + storedAtDateTime.toLocaleString(DateTime.TIME_SIMPLE) : '';
    return formatted;
}

function deliverySortColumn(delivery: Delivery, column: string): string {
    switch (column) {
        case 'deliveryId':
            return (delivery.metadata && delivery.metadata.foreignId) || (delivery.delivery && delivery.delivery.deliveryId) || "";
        case 'delivery':
            if (delivery.order) {
                return delivery.order.description || delivery.order.orderId || '';
            }
            return '';
        case 'recipient':
            if (delivery.recipients && delivery.recipients.length) {
                return delivery.recipients.map((r) => r.recipientName || r.pickupKey || '').join();
            }
            return '';
        case 'pickedUpAt':
            return delivery.delivery.pickedUpAt || '';
        case 'box':
            return delivery.delivery.boxNumber || '';
        case 'storedAt':
            return delivery.delivery.storedAt || '';
        default:
            throw new Error(`There is no sort configuration for column ${column}`);
    }
}

function toDateCompare(date: string): DateTime {
    let ret = DateTime.fromISO(date)
    if (!ret.isValid)
        ret = DateTime.now();
    return ret;
}

export function compareDelivery(a: Delivery, b: Delivery, sort: TableSort): number {
    const aSortValue = deliverySortColumn(a, sort.column);
    const bSortValue = deliverySortColumn(b, sort.column);
    let res: number
    if (sort.column == 'box') {
        res = naturalCompare(aSortValue, bSortValue);
    } else if (sort.column == 'storedAt') {
        res = toDateCompare(aSortValue).diff(toDateCompare(bSortValue)).toMillis();
    } else {
        res = aSortValue.localeCompare(bSortValue);
    }

    if (sort.order == 'desc') {
        res *= -1;
    }
    return res;
}

export function deliveryFromBookmark(bookmark: Bookmark): Delivery {
    return {
        bookmark: bookmark.id,
        delivery: {
            deliveryId: uuidv4(),
            isCreatedOnCube: true,
            state: DeliveryState.Created,
			accessCode: bookmark.fixedPickupCode,
        },
        order: {
            orderId: bookmark.id,
            description: bookmark.label
        },
        recipients: bookmark.recipients,
        storage: {
		}
    };
}

export function filterRecipients(recipients: Recipient[], filter: string, minChars: number = 3): Recipient[] {
    filter = filter.trim().toLowerCase();
    if(!filter || filter.length < minChars) {
        return [];
    }
    return recipients.filter((r) => {
        if(r.recipientName && r.recipientName.toLowerCase().indexOf(filter) > -1) {
            return true;
        }
        if(r.email && r.email.toLowerCase().indexOf(filter) > -1) {
            return true;
        }
        return false;
    });
}

export function isAnyState(deliveryState: DeliveryState, states: DeliveryState[]): boolean {
    return states.indexOf(deliveryState) > -1;
}
